/* ============================================================
   Chill Country Rewards — storefront widget
   Vanilla JS, no dependencies. Consumes the App-Proxy API at
   /apps/rewards/* (relative, same-origin; Shopify signs identity
   server-side — we never send a customer id from JS).

   Hard rules honored here:
   - enabled:false or /me failure  → render nothing (root removed).
   - identity comes only from the API / Liquid, never from JS.
   - the API contract is fixed; we never compute balances client-side.
   ============================================================ */
(function () {
  'use strict';

  var API = {
    me: '/apps/rewards/me',
    redeem: '/apps/rewards/redeem',
    reverse: '/apps/rewards/redeem/reverse',
    referralClaim: '/apps/rewards/referral-claim',
    referralInfo: '/apps/rewards/referral-info'
  };

  // Pre-cutover dev-theme preview: when the theme setting injects
  // window.CCR_PREVIEW='store_credit', every /me call asks the dashboard for the
  // store-credit payload (read-only) so the UNPUBLISHED dev theme renders the credit
  // UI for review. The live theme leaves this off and gets the normal (post-cutover
  // armed) payload. Once store_credit_enabled is armed the param is moot.
  var PREVIEW = (typeof window !== 'undefined' && window.CCR_PREVIEW === 'store_credit');
  function meUrl() { return API.me + (PREVIEW ? '?preview=store_credit' : ''); }
  // The mutating endpoints carry the same flag so the subscription convert/undo
  // take the store-credit branch on the dev theme before cutover (mirrors meUrl).
  function redeemUrl() { return API.redeem + (PREVIEW ? '?preview=store_credit' : ''); }
  function reverseUrl() { return API.reverse + (PREVIEW ? '?preview=store_credit' : ''); }

  // ---- small helpers --------------------------------------------------------
  function $(sel, root) { return (root || document).querySelector(sel); }
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  // "$5" for whole dollars, "$5.50" otherwise. Mirrors prototype tier copy.
  function usd(n) {
    var v = Number(n) || 0;
    return '$' + (v % 1 === 0 ? v.toFixed(0) : v.toFixed(2));
  }
  function usd2(n) { return '$' + (Number(n) || 0).toFixed(2); }

  // ---- subscription helpers (PR 2) -----------------------------------------
  // An active subscriber has a subscription WITH an upcoming charge to discount.
  function isActiveSub(me) {
    return !!(me && me.subscription && me.subscription.active === true);
  }
  // The subscriber conversion offer (store-credit era): the largest amount of
  // store credit that fits both the next-charge subtotal and the customer's credit
  // balance. Floored to whole cents so we never over-apply. Returns null when
  // there's nothing to offer (no active sub / no credit). The server independently
  // re-validates + caps the amount.
  function subOffer(me) {
    if (!isActiveSub(me)) return null;
    var sub = me.subscription;
    if (typeof sub.nextChargeSubtotal !== 'number') return null;
    var credit = typeof me.creditUsd === 'number' ? me.creditUsd : 0;
    var amount = Math.floor(Math.min(credit, sub.nextChargeSubtotal) * 100) / 100;
    if (amount < 0.01) return null;
    return {
      amountUsd: amount,
      subtotal: sub.nextChargeSubtotal,
      newTotal: Math.round((sub.nextChargeSubtotal - amount) * 100) / 100,
      date: sub.nextChargeDate,
    };
  }
  // "2026-06-19" -> "Jun 19". Parsed by parts so a TZ offset can't shift the day
  // (new Date('2026-06-19') is UTC-midnight and renders as the prior day west of
  // UTC — the same class of bug the dashboard's noon-UTC anchors avoid).
  var CCR_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  function fmtChargeDate(s) {
    if (!s) return '';
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
    if (!m) return String(s);
    var mo = parseInt(m[2], 10) - 1;
    var day = parseInt(m[3], 10);
    if (mo < 0 || mo > 11) return String(s);
    return CCR_MONTHS[mo] + ' ' + day;
  }

  // "2026-07-17..." -> "Jul 17" (abbreviated month). Parsed by parts so a TZ
  // offset can't shift the day. Returns '' for empty/unparseable input.
  var CCR_MON_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function fmtBogoDate(s) {
    if (!s) return '';
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
    if (!m) return '';
    var mo = parseInt(m[2], 10) - 1;
    var day = parseInt(m[3], 10);
    if (mo < 0 || mo > 11) return '';
    return CCR_MON_ABBR[mo] + ' ' + day;
  }

  // ---- referral BOGO codes (shared by drawer + landing) ---------------------
  // Builds ONE code row for an entry of me.referralBogoCodes:
  //   { code, expiresAt, expired, kind:'earned'|'friend' }
  // `rowCls`/`monoCls`/`subCls` let each surface match its native code-row chrome
  // (drawer .ccr-code-row vs landing .ccr-lp-code). The whole row is a copy button
  // that copies `code` with the same checkmark feedback as the referral-link field.
  function ccrBogoRow(c, classes) {
    var kindLabel = c.kind === 'friend' ? 'From your friend' : 'You earned this';
    var expClass = c.expired ? ' ccr-bogo-row--expired' : '';
    var row = el('button', classes.row + ' ccr-bogo-row' + expClass);
    row.type = 'button';
    row.setAttribute('aria-label', 'Copy BOGO code ' + c.code);
    var expHtml = c.expired
      ? '<span class="ccr-bogo-exp ccr-bogo-exp--expired">Expired</span>'
      : (function () {
          var d = fmtBogoDate(c.expiresAt);
          return d ? '<span class="ccr-bogo-exp">Expires ' + esc(d) + '</span>' : '';
        })();
    var info = el('div', null,
      '<div class="' + classes.mono + '">' + esc(c.code) + '</div>' +
      '<div class="' + classes.sub + ' ccr-bogo-meta">' +
        '<span class="ccr-bogo-kind">' + esc(kindLabel) + '</span>' + expHtml +
      '</div>');
    row.appendChild(info);
    var copyIc = el('span', 'ccr-bogo-copyic', null);
    copyIc.setAttribute('aria-hidden', 'true');
    var copyMarkup = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>';
    var checkMarkup = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg><span class="ccr-copied-label">Copied!</span>';
    copyIc.innerHTML = copyMarkup;
    row.appendChild(copyIc);
    row.addEventListener('click', function (e) {
      e.stopPropagation();
      copyText(c.code, null);
      row.classList.add('ccr-copied');
      copyIc.innerHTML = checkMarkup;
      setTimeout(function () {
        row.classList.remove('ccr-copied');
        copyIc.innerHTML = copyMarkup;
      }, 1600);
    });
    return row;
  }

  // Render the BOGO-codes section into `wrap`, toggling `section` visibility.
  // Show only when there's at least one code; hide entirely otherwise.
  function ccrRenderBogo(section, wrap, codes, classes) {
    if (!wrap) return;
    var list = Array.isArray(codes) ? codes : [];
    if (!list.length) {
      wrap.innerHTML = '';
      if (section) section.hidden = true;
      return;
    }
    if (section) section.hidden = false;
    wrap.innerHTML = '';
    list.forEach(function (c) { wrap.appendChild(ccrBogoRow(c, classes)); });
  }

  // A receipt-style math block (label left, value right, dashed line before the
  // total) — shared by the subscription apply card + the success card.
  function buildReceipt(subtotal, amountUsd, newTotal, dateStr) {
    var r = el('div', 'ccr-receipt');
    r.innerHTML =
      '<div class="ccr-receipt-row"><span>Next order total</span><span>' + esc(usd2(subtotal)) + '</span></div>' +
      '<div class="ccr-receipt-row"><span>Rewards credit</span><span class="ccr-neg">−' + esc(usd2(amountUsd)) + '</span></div>' +
      '<div class="ccr-receipt-row ccr-receipt-total"><span>New total</span><span class="ccr-pos">' + esc(usd2(newTotal)) + '</span></div>' +
      (dateStr ? '<div class="ccr-receipt-foot">Applies to your ' + esc(dateStr) + ' order</div>' : '');
    return r;
  }

  // ---- shared credit cards --------------------------------------------------
  // These cards render on the drawer and the landing page. Each takes its data + a
  // callback that wires the surface-specific POST, so the markup stays identical
  // everywhere while the action is owned by the caller.

  // Navy "apply $X of your credit to your next subscription order" card: receipt +
  // apply button + the "fixed amount" note. onApply(fail) runs the POST; fail(msgHtml)
  // is called ONLY on failure (re-enables the button + shows the error). This is the
  // ONE remaining redemption action — one-time orders apply credit automatically at
  // checkout, so there's no one-time card anymore.
  function ccrSubApplyCard(offer, onApply) {
    var card = el('div', 'ccr-subcard');
    card.appendChild(el('div', 'ccr-subcard-h', 'Apply to your next subscription order.'));
    card.appendChild(buildReceipt(offer.subtotal, offer.amountUsd, offer.newTotal, fmtChargeDate(offer.date)));
    var btn = el('button', 'ccr-subcard-btn', 'Apply To Subscription Order');
    btn.type = 'button';
    var note = el('div', 'ccr-subcard-note');
    note.textContent = 'This applies a fixed ' + usd2(offer.amountUsd) + ' discount to your next renewal. Any leftover credit stays on your account.';
    btn.addEventListener('click', function () {
      if (btn.disabled) return;
      btn.disabled = true; btn.textContent = 'Applying…';
      onApply(function (msg) {
        btn.disabled = false; btn.textContent = 'Apply To Subscription Order';
        if (msg) { note.innerHTML = msg; note.className = 'ccr-subcard-note ccr-subcard-note--err'; }
      });
    });
    card.appendChild(btn);
    card.appendChild(note);
    return card;
  }

  // The PDP the Shop Now buttons point at. Single source of truth.
  var CCR_SHOP_URL = 'https://chillcountry.com/products/chill-countrys-fast-acting-organic-edibles-gummies';

  // White info card: store credit applies automatically at checkout on one-time
  // orders (no redemption action). Shown when the customer has credit.
  function ccrAutoApplyCard(creditUsd) {
    var card = el('div', 'ccr-onetime ccr-autoapply ccr-onetime--centered');
    var ic = el('div', 'ccr-onetime-ic',
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="12" x="2" y="6" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></svg>');
    ic.setAttribute('aria-hidden', 'true');
    card.appendChild(ic);
    card.appendChild(el('div', 'ccr-onetime-h',
      'Use your <span class="ccr-pos">' + esc(usd2(creditUsd)) + '</span> credit at checkout'));
    card.appendChild(el('div', 'ccr-onetime-d',
      'Load up your cart and proceed to checkout. You’ll see an option under Payment Methods where you can choose to use your credit balance.'));
    var btn = el('a', 'ccr-onetime-btn');
    btn.href = CCR_SHOP_URL;
    btn.textContent = 'Shop Now';
    card.appendChild(btn);
    return card;
  }

  // White empty-state card when there's no credit yet: a bold "Shop now to earn"
  // headline + the earn explainer + a Shop Now button. Shared by drawer + landing.
  function ccrShopNowCard() {
    var card = el('div', 'ccr-onetime ccr-shopnow');
    card.appendChild(el('div', 'ccr-onetime-h', 'Shop now to earn store credit'));
    card.appendChild(el('div', 'ccr-onetime-d', 'Earn 10% cash back on every purchase.'));
    var btn = el('a', 'ccr-onetime-btn');
    btn.href = CCR_SHOP_URL;
    btn.textContent = 'Shop Now';
    card.appendChild(btn);
    return card;
  }

  // Green "✓ $X applied to your [date] order" card + Undo. onUndo(done) runs the
  // reverse POST (re-credits the store credit); done(err) re-enables (err → "Undo failed").
  function ccrSubAppliedCard(amountUsd, dateStr, onUndo) {
    var card = el('div', 'ccr-subapplied');
    card.appendChild(el('div', 'ccr-subapplied-h',
      '✓ ' + esc(usd2(amountUsd)) + ' in credit applied to your ' + (dateStr ? esc(dateStr) + ' ' : 'next ') + 'order'));
    var undo = el('button', 'ccr-subapplied-undo', 'Undo');
    undo.type = 'button';
    undo.addEventListener('click', function () {
      if (undo.disabled) return;
      undo.disabled = true; undo.textContent = 'Undoing…';
      onUndo(function (err) { undo.disabled = false; undo.textContent = err ? 'Undo failed' : 'Undo'; });
    });
    card.appendChild(undo);
    return card;
  }

  function copyText(text, btn, doneLabel) {
    var original = btn ? btn.textContent : null;
    function flash() {
      if (!btn) return;
      btn.textContent = doneLabel || 'Copied!';
      setTimeout(function () { if (btn) btn.textContent = original; }, 1600);
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(flash, function () { fallbackCopy(text); flash(); });
    } else {
      fallbackCopy(text);
      flash();
    }
  }
  function fallbackCopy(text) {
    var ta = el('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch (e) { /* no-op */ }
    document.body.removeChild(ta);
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  // ===========================================================================
  // Panel widget (launcher + slide-over / bottom sheet)
  // ===========================================================================
  function initWidget(root) {
    var loginUrl = root.getAttribute('data-login-url') || '/account/login';
    var liquidLoggedIn = root.getAttribute('data-logged-in') === 'true';

    var launcher = $('[data-ccr-launcher]', root);
    var pip = $('[data-ccr-pip]', root);
    var overlay = $('[data-ccr-overlay]', root);
    var panel = $('[data-ccr-panel]', root);
    var scroll = $('[data-ccr-scroll]', root);
    var hiEl = $('[data-ccr-hi]', root);
    var balEl = $('[data-ccr-balance]', root);
    var usdEl = $('[data-ccr-usd]', root);
    var panelView = $('[data-ccr-view="panel"]', root);
    var flowView = $('[data-ccr-view="flow"]', root);

    // External openers + points bubbles (e.g. the header rewards star icon),
    // which live outside .ccr-root.
    var headerIcons = document.querySelectorAll('[data-ccr-header-icon]');
    var headerPips = document.querySelectorAll('[data-ccr-header-pip]');

    var state = { me: null, firstName: root.getAttribute('data-first-name') || '' };

    // ---- panel loading overlay ---------------------------------------------
    // A branded spinner + label shown over the logged-in panel content while
    // /me is (re)fetched, so an open never reveals stale/empty content. Built
    // once, lazily, inside the scroll area (the panel's positioning context);
    // no-ops on the logged-out shell where there's nothing to refresh.
    var panelLoader = null;
    function ensurePanelLoader() {
      if (panelLoader || !scroll) return panelLoader;
      panelLoader = el('div', 'ccr-panel-loading', null);
      panelLoader.setAttribute('aria-hidden', 'true');
      panelLoader.innerHTML =
        '<span class="ccr-panel-loading__ring" aria-hidden="true"></span>' +
        '<span class="ccr-panel-loading__label">Loading your rewards…</span>';
      scroll.appendChild(panelLoader);
      return panelLoader;
    }
    function showPanelLoading() {
      if (!hiEl) return; // logged-out shell — nothing to load
      var l = ensurePanelLoader();
      if (l) l.classList.add('is-visible');
    }
    function hidePanelLoading() {
      if (panelLoader) panelLoader.classList.remove('is-visible');
    }

    // Set the balance on the launcher pip + any header points bubbles.
    // Only shown when the customer actually has points (> 0).
    // Abbreviate large balances so the bubble stays compact: 1342 -> "1.3k".
    function abbrevPts(n) {
      if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'm';
      if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
      return String(n);
    }

    function setBalancePips(creditUsd) {
      // Show the store-credit DOLLAR value, e.g. "$64" (whole dollars for a compact pip).
      var dollars = typeof creditUsd === 'number' ? Math.floor(creditUsd) : 0;
      var has = dollars >= 1;
      var label = has ? ('$' + abbrevPts(dollars)) : '';
      if (pip) {
        if (has) { pip.textContent = label; pip.hidden = false; } else { pip.hidden = true; }
      }
      // Re-query live (not the cached NodeList) so a re-rendered header bubble
      // still gets set. Setting text fills the .cart-count-bubble; clearing it
      // makes the bubble :empty, which the theme hides automatically.
      document.querySelectorAll('[data-ccr-header-pip]').forEach(function (b) {
        b.textContent = label;
      });
    }

    // ---- open / close -------------------------------------------------------
    function openPanel() {
      overlay.hidden = false;
      // force reflow so the transition runs from the hidden state
      void overlay.offsetWidth;
      overlay.classList.add('ccr-open');
      launcher.setAttribute('aria-expanded', 'true');
      // Lock page scroll behind the drawer (the panel scrolls internally via .ccr-scroll).
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeydown);
      // Re-fetch /me on every open so the panel reflects any cross-surface change
      // (e.g. points just applied/removed from the cart drawer) without a full
      // page refresh. fetchMeAndRefresh no-ops on logged-out/disabled /me. The
      // init fetch still handles first paint; this keeps it fresh thereafter.
      // Show the branded loader over the content first so the panel never
      // reveals a stale balance/codes between opens; fetchMeAndRefresh clears it.
      showPanelLoading();
      fetchMeAndRefresh();
    }
    function closePanel() {
      overlay.classList.remove('ccr-open');
      launcher.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      // return to the default view once closed
      showPanelView();
      var done = function () {
        overlay.hidden = true;
        overlay.removeEventListener('transitionend', done);
      };
      overlay.addEventListener('transitionend', done);
      // fallback in case transitionend doesn't fire
      setTimeout(function () { overlay.hidden = true; }, 320);
    }
    function onKeydown(e) { if (e.key === 'Escape') closePanel(); }

    // The floating widget has a launcher + overlay (slide-over chrome). The
    // inline landing instance has neither — it just renders the logged-in
    // content in place — so all the open/close wiring is gated on `overlay`.
    var isFloating = !!(launcher && overlay);
    if (isFloating) {
      launcher.addEventListener('click', openPanel);
      root.addEventListener('click', function (e) {
        if (e.target.closest('[data-ccr-close]')) { closePanel(); }
      });
      // Close when clicking the dimmed page area. Match only a DIRECT click on
      // the overlay/backdrop — not a bubbled click from a panel child. (Using
      // closest('[data-ccr-panel]') broke on mobile: tapping the copy icon swaps
      // its innerHTML, detaching the tapped node, so closest() returned null and
      // the panel closed.)
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay || (e.target.classList && e.target.classList.contains('ccr-backdrop'))) {
          closePanel();
        }
      });
      // External openers: the header rewards icon + any [data-ccr-open] trigger.
      document.querySelectorAll('[data-ccr-header-icon], [data-ccr-open]').forEach(function (btn) {
        btn.addEventListener('click', function (e) { e.preventDefault(); openPanel(); });
      });
    }

    // ---- view switching -----------------------------------------------------
    function showPanelView() {
      if (flowView) { flowView.hidden = true; flowView.innerHTML = ''; }
      if (panelView) panelView.hidden = false;
      renderHeadNormal();
    }
    function showFlowView() {
      if (panelView) panelView.hidden = true;
      if (flowView) flowView.hidden = false;
      if (scroll) scroll.scrollTop = 0;
    }

    // ---- header -------------------------------------------------------------
    function renderHeadNormal() {
      if (!hiEl) return; // logged-out shell has no dynamic head
      hiEl.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/></svg>REWARDS CREDIT BALANCE';
      var credit = state.me && typeof state.me.creditUsd === 'number' ? state.me.creditUsd : null;
      balEl.style.fontFamily = 'ui-monospace, SFMono-Regular, Menlo, monospace';
      if (credit == null) {
        balEl.textContent = '—';
        usdEl.textContent = '';
      } else {
        balEl.textContent = usd2(credit); // "$100.00" / "$0.00", mono
        usdEl.textContent = credit >= 0.01 ? 'Use as cash credit towards your next order at checkout.' : '';
      }
    }

    // ---- logged-in panel content from /me -----------------------------------
    function renderLoggedIn() {
      renderHeadNormal();
      if (state.me) setBalancePips(state.me.creditUsd);
      renderRedeem();
      renderBogo();
      renderReferral();
    }

    function renderBogo() {
      ccrRenderBogo(
        $('[data-ccr-bogo-section]', root),
        $('[data-ccr-bogo]', root),
        state.me && state.me.referralBogoCodes,
        { row: 'ccr-code-row', mono: 'ccr-code-mono', sub: 'ccr-code-sub' }
      );
    }

    // Store-credit "Use your credit" section: the subscription convert card (or
    // applied/undo) when the customer has an upcoming renewal — then the "applies
    // automatically at checkout" note for one-time orders. No tiers, no one-time
    // redeem action (checkout owns one-time). Spec: rewards-store-credit-revamp D3/D4.
    function renderRedeem() {
      var wrap = $('[data-ccr-tiers]', root);
      if (!wrap) return;
      wrap.innerHTML = '';
      var me = state.me || {};
      var credit = typeof me.creditUsd === 'number' ? me.creditUsd : 0;
      var sub = me.subscription;
      var subShown = false;

      if (sub && sub.applied) {
        wrap.appendChild(buildSubAppliedCard(sub.applied));
        subShown = true;
      } else {
        var offer = state.subApplied ? null : subOffer(me);
        if (offer) { wrap.appendChild(buildSubApplyCard(offer)); subShown = true; }
      }

      if (credit >= 0.01) {
        if (subShown) wrap.appendChild(el('div', 'ccr-or', '<span>OR</span>'));
        wrap.appendChild(ccrAutoApplyCard(credit));
      } else if (!subShown) {
        // No credit and no subscription offer → earn note.
        wrap.appendChild(ccrShopNowCard());
      }
    }

    // "Points already applied to your next subscription order" — with Undo (D6).
    function buildSubAppliedCard(applied) {
      var dateStr = fmtChargeDate(state.me && state.me.subscription && state.me.subscription.nextChargeDate);
      return ccrSubAppliedCard(applied.amountUsd, dateStr, function (done) {
        doSubReverse(applied.redemptionId, function () { done(true); });
      });
    }

    function doSubReverse(redemptionId, onFail) {
      postJSON(reverseUrl(), { kind: 'subscription', subscriptionRedemptionId: redemptionId })
        .then(function (res) {
          if (res.status === 200) {
            // Credit reverse re-credits store credit server-side; the authoritative
            // new creditUsd comes from the /me reconcile (not the reverse response).
            if (state.me) {
              if (state.me.subscription) state.me.subscription.applied = null;
              state.subApplied = false;
            }
            renderLoggedIn();   // immediately re-shows the apply offer card
            fetchMeAndRefresh(); // reconcile to authoritative /me (fresh creditUsd)
            return;
          }
          onFail(true);
        })
        .catch(function () { onFail(true); });
    }

    function renderReferral() {
      var urlEl = $('[data-ccr-refer-url]', root);
      var field = $('[data-ccr-refer-copy]', root);
      var section = $('[data-ccr-refer-section]', root);
      var ref = state.me && state.me.referral;
      if (!ref || !ref.url) { if (section) section.hidden = true; return; }
      if (section) section.hidden = false;
      if (urlEl) urlEl.textContent = ref.url;
      if (field && !field.dataset.bound) {
        field.dataset.bound = '1';
        // Whole field copies the link; flash the icon to a checkmark on success.
        var icon = $('[data-ccr-refer-copyicon]', field);
        var copyMarkup = icon ? icon.innerHTML : null;
        var checkMarkup = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg><span class="ccr-copied-label">Copied!</span>';
        field.addEventListener('click', function (e) {
          e.stopPropagation();
          copyText(urlEl ? urlEl.textContent : ref.url, null);
          field.classList.add('ccr-copied');
          if (icon) icon.innerHTML = checkMarkup;
          setTimeout(function () {
            field.classList.remove('ccr-copied');
            if (icon && copyMarkup != null) icon.innerHTML = copyMarkup;
          }, 1600);
        });
      }
    }

    // ---- redeem flow (store credit) -----------------------------------------
    // Subscriber convert card (navy) — applies $X of credit to the next renewal;
    // the card IS the confirmation (no separate confirm step).
    function buildSubApplyCard(offer) {
      return ccrSubApplyCard(offer, function (fail) { doSubRedeem(offer.amountUsd, fail); });
    }

    // ---- subscription convert (store credit → next-renewal discount) --------
    // POSTs { target:'subscription', amountUsd }. onFail(msgHtml) — recoverable
    // error, credit NOT debited (incl. 409 conflict — surfaced directly, no code
    // fallback in the credit model).
    function doSubRedeem(amountUsd, onFail) {
      postJSON(redeemUrl(), { target: 'subscription', amountUsd: amountUsd })
        .then(function (res) {
          if (res.status === 200) {
            return res.json().then(function (data) { onSubRedeemSuccess(data); });
          }
          if (res.status === 409) {
            onFail('Your next order already has a discount, so we couldn’t add this. Your credit is still available to use at checkout on one-time orders.');
            return;
          }
          if (res.status === 422) {
            return res.json().catch(function () { return {}; }).then(function (b) {
              var code = ((b && b.error) || '').toString().toLowerCase();
              onFail(code.indexOf('insufficient') !== -1 ? 'You don’t have that much store credit.'
                : code.indexOf('exceeds') !== -1 ? 'That’s more than your next order total.'
                : 'We couldn’t apply that to your next order.');
            });
          }
          if (res.status === 401) {
            onFail('Your session expired. <a href="' + esc(loginUrl) + '">Log in</a> and try again.');
            return;
          }
          // 502 apply_failed and anything else: credit was NOT debited.
          onFail('Something went wrong. Your credit was <b>not</b> used. Please try again.');
        })
        .catch(function () {
          onFail('Something went wrong. Your credit was <b>not</b> used. Please try again.');
        });
    }

    // Re-fetch /me so the panel reflects the authoritative applied state (incl. the
    // redemptionId needed for Undo) immediately — so "Done" shows the applied card
    // without a refresh. Updates the hidden panel content; the flow/success view is
    // untouched until the customer taps Done.
    function fetchMeAndRefresh() {
      fetch(meUrl(), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (me) {
          if (!me || me.enabled !== true || me.loggedIn === false || me.model !== 'store_credit') { hidePanelLoading(); return; }
          state.me = me;
          state.subApplied = false; // me.subscription.applied is now authoritative
          setBalancePips(me.creditUsd);
          renderHeadNormal();
          renderRedeem();
          renderBogo();
          renderReferral();
          hidePanelLoading();
        })
        .catch(function () { hidePanelLoading(); });
    }

    function onSubRedeemSuccess(data) {
      // Authoritative response (no redemptionId in the body). Optimistically reflect
      // the applied state, then re-fetch /me for the real redemptionId + fresh credit.
      if (state.me && state.me.subscription) {
        state.me.subscription.applied = { redemptionId: null, amountUsd: Number(data.amountUsd) };
      }
      renderRedeem();
      fetchMeAndRefresh();

      var sub = state.me && state.me.subscription;
      var subtotal = sub && typeof sub.nextChargeSubtotal === 'number' ? sub.nextChargeSubtotal : null;
      var dateStr = fmtChargeDate(data.nextChargeDate || (sub && sub.nextChargeDate));
      flowView.innerHTML = '';
      var card = el('div', 'ccr-success');
      card.appendChild(el('div', 'ccr-ok', '✓'));
      card.appendChild(el('h4', null, esc(usd2(data.amountUsd)) + ' in credit applied!'));
      if (subtotal != null) {
        card.appendChild(buildReceipt(subtotal, Number(data.amountUsd), Math.round((subtotal - Number(data.amountUsd)) * 100) / 100, dateStr));
      }
      var acts = el('div', 'ccr-success-acts');
      var done = el('button', 'ccr-b-go', 'Done');
      done.type = 'button';
      done.addEventListener('click', showPanelView);
      acts.appendChild(done);
      card.appendChild(acts);
      flowView.appendChild(card);
      showFlowView();
    }

    // ---- boot: fetch /me, decide whether to render at all --------------------
    fetch(meUrl(), { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('me ' + res.status);
        return res.json();
      })
      .then(function (me) {
        if (!me || me.enabled !== true) {
          // Program off (or malformed) → render nothing.
          root.remove();
          if (isFloating) headerIcons.forEach(function (el) { el.remove(); });
          return;
        }
        state.me = me;
        // Reveal this instance (launcher/panel, or the inline landing card).
        root.hidden = false;
        if (isFloating) headerIcons.forEach(function (el) { el.hidden = false; });
        // Credit model only renders for a logged-in customer on the store-credit
        // payload; anything else (e.g. legacy points shape) leaves the static shell.
        var isLoggedIn = me.loggedIn !== false && me.model === 'store_credit';
        if (isLoggedIn) {
          renderLoggedIn();
        }
        // logged-out shell is static Liquid; nothing to populate.
      })
      .catch(function () {
        // /me failed → render nothing (safety net).
        root.remove();
        if (isFloating) headerIcons.forEach(function (el) { el.remove(); });
      });
  }

  // ===========================================================================
  // Referral landing — /pages/refer
  // ===========================================================================
  function initReferLanding(land) {
    var nameEl = $('[data-ccr-ref-name]', land);
    var form = $('[data-ccr-claim]', land);
    var emailEl = $('[data-ccr-claim-email]', land);
    var result = $('[data-ccr-claim-result]', land);
    if (!form) return;

    var params = new URLSearchParams(window.location.search);
    var ref = (params.get('ref') || '').trim();

    // Validate the handle server-side and use the verified first name. Never
    // echo the raw URL value into the page. The markup's default "A friend"
    // stays unless the dashboard confirms a valid referrer. The endpoint won't
    // exist on the live proxy until the dashboard side ships, so any non-OK
    // response / network error / bad JSON is swallowed and the default stands.
    if (ref && nameEl) {
      fetch(API.referralInfo + '?ref=' + encodeURIComponent(ref), {
        headers: { 'Accept': 'application/json' }
      })
        .then(function (res) {
          // FAIL OPEN: any non-OK response → swallow (return null), so a backend
          // outage or the pre-launch period (endpoint not deployed yet) leaves
          // the normal generic page intact. Never surfaces the invalid state.
          if (!res.ok) return null;
          return res.json();
        })
        .then(function (info) {
          if (!info) return; // non-OK or already-null → generic page stands.
          if (info.valid === true) {
            // Valid referrer: use the verified first name; null name keeps "A friend".
            if (info.referrerFirstName) nameEl.textContent = info.referrerFirstName;
          } else if (info.valid === false) {
            // AUTHORITATIVE invalid handle (only from a successful response).
            showInvalidLink();
          }
          // Any other shape (no `valid` key, bad JSON resolved to non-object) →
          // leave the generic page untouched.
        })
        .catch(function () { /* network error / invalid JSON → leave generic page (fail open) */ });
    }

    // Renders the invalid-referral-link state into the existing result container.
    // Hides the claim form, body copy, and fine print, then replaces the page's
    // call-to-action with a friendly "this link isn't valid" card. Only ever
    // called on an explicit `valid === false` from a successful response.
    function showInvalidLink() {
      if (form) form.style.display = 'none';
      var fine = land.querySelector('.ccr-reffine');
      if (fine) fine.style.display = 'none';
      var sub = land.querySelector('[data-ccr-refsub]');
      if (sub) sub.style.display = 'none';
      if (nameEl) {
        var h = nameEl.closest('h1');
        if (h) h.style.display = 'none';
      }
      if (!result) return;
      result.innerHTML = '';
      var card = el('div', 'ccr-success');
      card.appendChild(el('h4', null, 'Hmm, this referral link isn’t valid'));
      card.appendChild(el('div', 'ccr-sub', 'Double-check the link your friend sent you.'));
      var acts = el('div', 'ccr-success-acts');
      var shop = el('a', 'ccr-b-go', 'Shop our gummies');
      shop.href = '/products/chill-countrys-fast-acting-organic-edibles-gummies';
      acts.appendChild(shop);
      card.appendChild(acts);
      result.appendChild(card);
      result.hidden = false;
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (emailEl.value || '').trim();
      var group = land.querySelector('.ccr-claim-group');
      // Validate before hitting the API (was failing silently on empty input).
      var valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!valid) {
        if (group) group.classList.add('ccr-invalid');
        showError(email ? 'Please enter a valid email address.' : 'Please enter your email address.');
        emailEl.focus();
        return;
      }
      // Block "+" alias addresses (used to self-refer, e.g. you+test@…).
      if (email.split('@')[0].indexOf('+') !== -1) {
        if (group) group.classList.add('ccr-invalid');
        showError('Please use your main email address. “+” aliases aren’t allowed.');
        emailEl.focus();
        return;
      }
      if (group) group.classList.remove('ccr-invalid');
      var btn = form.querySelector('button');
      var label = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Getting code…'; }
      clearError();

      postJSON(API.referralClaim, { ref: ref, email: email })
        .then(function (res) {
          if (res.status === 200) {
            return res.json().then(function (data) { showClaimSuccess(data); });
          }
          if (btn) { btn.disabled = false; btn.textContent = label; }
          if (res.status === 409) {
            // 409 = this email already claimed a code, or it belongs to someone
            // who has already ordered (the friend reward is for first orders).
            res.json().then(function (b) {
              var code = ((b && b.error) || '').toString().toLowerCase();
              showError(code === 'existing_customer'
                ? 'This offer is for first-time customers, and it looks like you’ve ordered with us before.'
                : 'Looks like this email already claimed a friend code.');
            }).catch(function () {
              showError('Looks like this email already claimed a friend code.');
            });
          } else if (res.status === 422) {
            // 422 = self-referral OR invalid link. On a referral page the ref
            // comes from a real share link, so this is almost always a
            // self-referral; only show the "invalid link" copy if the dashboard
            // explicitly flags it.
            res.json().then(function (b) {
              var code = ((b && b.error) || '').toString().toLowerCase();
              var invalid = code.indexOf('invalid') !== -1 || code.indexOf('not_found') !== -1;
              showError(invalid ? 'This referral link isn’t valid. Double-check it and try again.' : 'You can’t refer yourself!');
            }).catch(function () {
              showError('You can’t refer yourself!');
            });
          } else if (res.status === 429) {
            showError('Whoa there, too many tries. Give it a minute and try again.');
          } else {
            showError('Something went wrong on our end. Please try again in a bit.');
          }
        })
        .catch(function () {
          if (btn) { btn.disabled = false; btn.textContent = label; }
          showError('Something went wrong on our end. Please try again in a bit.');
        });
    });

    function clearError() {
      var ex = land.querySelector('.ccr-err');
      if (ex) ex.remove();
    }
    function showError(msg) {
      clearError();
      var e = el('div', 'ccr-err', esc(msg));
      e.style.marginTop = '14px';
      form.parentNode.insertBefore(e, form.nextSibling);
    }
    function showClaimSuccess(data) {
      if (form) form.style.display = 'none';
      var fine = land.querySelector('.ccr-reffine');
      if (fine) fine.style.display = 'none';
      var sub = land.querySelector('[data-ccr-refsub]');
      if (sub) sub.style.display = 'none';
      clearError();
      result.innerHTML = '';
      var card = el('div', 'ccr-success');
      card.appendChild(el('div', 'ccr-ok', '✓'));
      card.appendChild(el('h4', null, 'Here’s your ' + esc(data.rewardLabel || 'Buy One, Get One Free') + ' code!'));
      card.appendChild(el('div', 'ccr-sub', 'Good for ' + esc(String(data.expiryDays || 30)) + ' days. We emailed it to you, too.'));
      card.appendChild(el('div', 'ccr-big-code', esc(data.code)));
      var acts = el('div', 'ccr-success-acts');
      var shop = el('a', 'ccr-b-go', 'Start shopping');
      shop.href = '/discount/' + encodeURIComponent(data.code) + '?redirect=/products/chill-countrys-fast-acting-organic-edibles-gummies';
      acts.appendChild(shop);
      card.appendChild(acts);
      result.appendChild(card);
      result.hidden = false;
    }
  }

  // ===========================================================================
  // Rewards landing page (/pages/chill-country-rewards-program).
  // The scaffold is server-rendered; this hydrates the personalized layer from
  // /apps/rewards/me (balance, tier affordability + Redeem buttons, Your codes,
  // referral link). That data is dashboard-only / App-Proxy-fetched — never
  // available to Liquid — so only these bits populate after first paint.
  // ===========================================================================
  function initLanding(root) {
    // NB: do NOT name this param `el` — that would shadow the el() DOM-builder
    // helper and throw the moment we try to build an element.
    var balEl = $('[data-ccr-l-balance]', root);
    var usdEl = $('[data-ccr-l-usd]', root);
    var bogoSection = $('[data-ccr-l-bogo-section]', root);
    var bogoWrap = $('[data-ccr-l-bogo]', root);
    var referUrlEl = $('[data-ccr-l-refer-url]', root);
    var referField = $('[data-ccr-l-refer-copy]', root);
    var me = null;
    var subApplied = false; // suppress the convert card after a successful convert

    fetch(meUrl(), { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('me ' + r.status); return r.json(); })
      .then(function (data) {
        if (!data || data.enabled !== true || data.loggedIn === false || data.model !== 'store_credit') {
          clearLoading('—'); // leave the static scaffold; nothing personalized to show
          return;
        }
        me = data;
        renderBalance();
        renderRedeem(); // "Use your credit" — same builders as the drawer
        renderBogo();
        renderReferral();
      })
      .catch(function () { clearLoading('—'); });

    function clearLoading(fallback) {
      if (balEl) { balEl.classList.remove('ccr-lp-bal--loading'); if (fallback != null) balEl.textContent = fallback; }
      if (usdEl) { usdEl.classList.remove('ccr-lp-usd--loading'); usdEl.textContent = ''; }
    }

    // Reconcile to authoritative /me (fresh creditUsd + applied state) after a convert/undo.
    function refetch() {
      fetch(meUrl(), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          if (d && d.enabled === true && d.model === 'store_credit') { me = d; renderBalance(); renderRedeem(); }
        })
        .catch(function () {});
    }

    function renderBalance() {
      if (!balEl) return;
      balEl.classList.remove('ccr-lp-bal--loading');
      if (usdEl) usdEl.classList.remove('ccr-lp-usd--loading');
      var credit = typeof me.creditUsd === 'number' ? me.creditUsd : 0;
      balEl.style.fontFamily = 'ui-monospace, SFMono-Regular, Menlo, monospace';
      balEl.textContent = usd2(credit); // "$100.00" / "$0.00", mono
      if (usdEl) usdEl.textContent = credit >= 0.01 ? 'Use as cash credit towards your next order at checkout.' : '';
    }

    // "Use your credit" — the subscription convert card (or applied/Undo) + the
    // "applies automatically at checkout" note. Mirrors the drawer's renderRedeem.
    function renderRedeem() {
      var wrap = $('[data-ccr-l-subapply]', root);
      if (!wrap) return;
      wrap.innerHTML = '';
      var credit = typeof me.creditUsd === 'number' ? me.creditUsd : 0;
      var subShown = false;

      if (me.subscription && me.subscription.applied) {
        var dateStr = fmtChargeDate(me.subscription.nextChargeDate);
        wrap.appendChild(ccrSubAppliedCard(me.subscription.applied.amountUsd, dateStr, function (done) {
          postJSON(reverseUrl(), { kind: 'subscription', subscriptionRedemptionId: me.subscription.applied.redemptionId })
            .then(function (res) {
              if (res.status === 200) {
                me.subscription.applied = null; subApplied = false;
                renderBalance(); renderRedeem(); refetch();
                return;
              }
              done(true);
            })
            .catch(function () { done(true); });
        }));
        subShown = true;
      } else {
        var offer = subApplied ? null : subOffer(me);
        if (offer) {
          wrap.appendChild(ccrSubApplyCard(offer, function (fail) {
            postJSON(redeemUrl(), { target: 'subscription', amountUsd: offer.amountUsd })
              .then(function (res) {
                if (res.status === 200) { return res.json().then(function (d) { onLandingSubSuccess(d); }); }
                if (res.status === 409) { fail('Your next order already has a discount, so we couldn’t add this. Your credit is still available to use at checkout on one-time orders.'); return; }
                if (res.status === 422) {
                  return res.json().catch(function () { return {}; }).then(function (b) {
                    var code = ((b && b.error) || '').toString().toLowerCase();
                    fail(code.indexOf('insufficient') !== -1 ? 'You don’t have that much store credit.'
                      : code.indexOf('exceeds') !== -1 ? 'That’s more than your next order total.'
                      : 'We couldn’t apply that to your next order.');
                  });
                }
                fail(res.status === 401 ? 'Your session expired. Please log in again.' : 'Something went wrong. Your credit was <b>not</b> used. Please try again.');
              })
              .catch(function () { fail('Something went wrong. Your credit was <b>not</b> used. Please try again.'); });
          }));
          subShown = true;
        }
      }

      if (credit >= 0.01) {
        if (subShown) wrap.appendChild(el('div', 'ccr-or', '<span>OR</span>'));
        wrap.appendChild(ccrAutoApplyCard(credit));
      } else if (!subShown) {
        wrap.appendChild(ccrShopNowCard());
      }
    }

    // After a successful convert on the landing: reflect the applied state, then
    // reconcile to authoritative /me (fresh creditUsd + the real redemptionId).
    function onLandingSubSuccess(d) {
      if (me.subscription) me.subscription.applied = { redemptionId: null, amountUsd: Number(d.amountUsd) };
      subApplied = false;
      renderBalance();
      renderRedeem();
      refetch();
    }

    function renderBogo() {
      ccrRenderBogo(
        bogoSection, bogoWrap,
        me && me.referralBogoCodes,
        { row: 'ccr-lp-code', mono: 'ccr-lp-code__mono', sub: 'ccr-lp-code__sub' }
      );
    }

    function renderReferral() {
      if (!referUrlEl || !me.referral || !me.referral.url) return;
      referUrlEl.textContent = me.referral.url;
      if (referField && !referField.dataset.bound) {
        referField.dataset.bound = '1';
        var icon = $('[data-ccr-refer-copyicon]', referField);
        var copyMarkup = icon ? icon.innerHTML : null;
        var checkMarkup = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg><span class="ccr-copied-label">Copied!</span>';
        referField.addEventListener('click', function () {
          copyText(referUrlEl.textContent, null);
          referField.classList.add('ccr-copied');
          if (icon) icon.innerHTML = checkMarkup;
          setTimeout(function () {
            referField.classList.remove('ccr-copied');
            if (icon && copyMarkup != null) icon.innerHTML = copyMarkup;
          }, 1600);
        });
      }
    }

  }

  // ===========================================================================
  // PDP credit block (under the price). Logged-in only: store-credit balance + the
  // "applied automatically at checkout" line. The subscription convert lives in the
  // drawer/landing (it reads as confusing on the PDP). The gift icon is static
  // Liquid in the markup; JS only fills the text.
  // ===========================================================================
  function initPdp(root) {
    var balEl = $('[data-ccr-pdp-bal]', root);

    fetch(meUrl(), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        // Nothing to show for logged-out / disabled / no credit.
        if (!data || data.enabled !== true || data.loggedIn === false || data.model !== 'store_credit' || typeof data.creditUsd !== 'number' || data.creditUsd < 0.01) {
          root.remove();
          return;
        }
        if (balEl) {
          balEl.innerHTML = 'You have <b class="ccr-pos">' + esc(usd2(data.creditUsd)) + '</b> in rewards credit, available to use at checkout.';
        }
        root.hidden = false;
      })
      .catch(function () { root.remove(); });
  }

  // ===========================================================================
  function boot() {
    var root = $('[data-ccr-root]');
    if (root) initWidget(root);
    var landing = $('[data-ccr-landing]');
    if (landing) initLanding(landing);
    var land = $('[data-ccr-refer-landing]');
    if (land) initReferLanding(land);
    var pdp = $('[data-ccr-pdp]');
    if (pdp) initPdp(pdp);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
